import { useState } from "react";
import { LocalizedLink } from "../i18n/LocalizedLink";
import { LANDING_PAGE_ROUTES, GUIDE_ROUTES } from "@plaatplan/i18n";
import packageJson from "../../package.json";
import { COLOFON_LINKS } from "../content/siteContent";
import { getSupportLinks, shouldShowSupport } from "../content/supportConfig";
import { isMobileAppPromoEnabled } from "../config/featureFlags";
import { useLocale } from "../i18n/useLocale";
import { SupportModal } from "./SupportModal";

const SEO_OPEN_KEY = "plaatplan.seo-section.open";

function readStoredSeoOpen() {
  try {
    return localStorage.getItem(SEO_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

function FooterSeoIntro() {
  const { display } = useLocale();
  const defaultOpen = readStoredSeoOpen();

  return (
    <details
      className="app-colofon-seo"
      defaultOpen={defaultOpen}
      onToggle={(event) => {
        try {
          localStorage.setItem(SEO_OPEN_KEY, event.currentTarget.open ? "1" : "0");
        } catch {
          /* ignore */
        }
      }}
    >
      <summary className="app-colofon-seo-summary">{display.t("web.footer.seoSummary")}</summary>
      <p className="app-colofon-seo-text">{display.t("web.footer.seoText")}</p>
    </details>
  );
}

export function SiteFooter({ onOpenPage, showSeoIntro = false }) {
  const { display } = useLocale();
  const year = new Date().getFullYear();
  const supportLinks = getSupportLinks({ display });
  const showSupport = shouldShowSupport();
  const showAppPromo = isMobileAppPromoEnabled();
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <footer className="app-colofon">
      <div className="app-colofon-inner">
        <div className="app-colofon-grid">
          <div className="app-colofon-brand-col">
            <p className="app-colofon-brand">
              <strong>Plaatplan</strong>
              <span className="app-colofon-version">v{packageJson.version}</span>
            </p>
            <p className="app-colofon-copy">{display.t("web.footer.tagline")}</p>
            {showSeoIntro && <FooterSeoIntro />}
          </div>

          <nav className="app-colofon-col" aria-label={display.t("web.footer.guides")}>
            <p className="app-colofon-col-title" id="colofon-gidsen">
              {display.t("web.footer.guides")}
            </p>
            <ul className="app-colofon-col-list" aria-labelledby="colofon-gidsen">
              {LANDING_PAGE_ROUTES.map((page) => (
                <li key={page.slug}>
                  <LocalizedLink to={page.path} className="app-colofon-link app-colofon-route-link">
                    {display.t(`web.landing.nav.${page.slug}`)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="app-colofon-col" aria-label={display.t("web.footer.handbook")}>
            <p className="app-colofon-col-title" id="colofon-handleiding">
              {display.t("web.footer.handbook")}
            </p>
            <ul className="app-colofon-col-list" aria-labelledby="colofon-handleiding">
              {GUIDE_ROUTES.map((guide) => (
                <li key={guide.path}>
                  <LocalizedLink to={guide.path} className="app-colofon-link app-colofon-route-link">
                    {display.t(`web.guides.nav.${guide.pageId}`)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </nav>

          {onOpenPage && (
            <nav className="app-colofon-col" aria-label={display.t("web.footer.info")}>
              <p className="app-colofon-col-title" id="colofon-info">
                {display.t("web.footer.info")}
              </p>
              <ul className="app-colofon-col-list" aria-labelledby="colofon-info">
                {COLOFON_LINKS.map((link) => (
                  <li key={link.pageId}>
                    <button
                      type="button"
                      className="app-colofon-link"
                      onClick={() => onOpenPage(link.pageId)}
                    >
                      {display.t(`web.footer.infoLinks.${link.pageId}`)}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>

        {showSupport && (
          <div className="app-colofon-support" aria-label={display.t("web.footer.supportRegion")}>
            <div className="app-colofon-support-copy">
              <p className="app-colofon-support-title">{display.t("web.footer.supportTitle")}</p>
              <p className="app-colofon-support-text">
                {display.t(showAppPromo
                  ? "web.footer.supportTextWithApp"
                  : "web.footer.supportText")}
              </p>
            </div>
            <button
              type="button"
              className="app-colofon-support-btn"
              onClick={() => setSupportOpen(true)}
            >
              {display.t("web.footer.supportButton")}
            </button>
          </div>
        )}

        <div className="app-colofon-bottom">
          <p className="app-colofon-meta">
            <span>{display.t("web.footer.copyright", { year })}</span>
            <span className="app-colofon-sep" aria-hidden="true">·</span>
            <span>{display.t("web.footer.freeInBrowser")}</span>
            <span className="app-colofon-sep" aria-hidden="true">·</span>
            <span>{display.t("web.footer.noAccount")}</span>
          </p>
        </div>
      </div>

      {supportOpen && (
        <SupportModal
          supportLinks={supportLinks}
          onFeedback={() => onOpenPage?.("feedback")}
          onClose={() => setSupportOpen(false)}
        />
      )}
    </footer>
  );
}
